import { FC, useEffect, useState } from 'react';
import { LabeledValue } from '../account-info/AccountInfo';
import QbEditModal from './QbEditModal';
import Loader from '../loader/Loader';
import { EditSVG } from '../svgs';
import { QBCustomerInformation } from '../../types/interfaces/quickbooks.interface';
import { showToast } from '../../services/toast.service';
import { getQuickBooksCustomerInfo } from '../../api/quickbooksApi';
import { toastMessages } from '../../constants/errorMessages';
import { LABEL_CONSTANTS } from '../../constants/common';
import './qbCustomerInfo.scss';

interface QBCustomerInfoProps {
  accountId: number;
}

const QBCustomerInfo: FC<QBCustomerInfoProps> = ({ accountId }) => {
  const [customerInfo, setCustomerInfo] = useState<QBCustomerInformation | undefined>(undefined);
  const [isOpenQbEditModal, setIsOpenQbEditModal] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    setCustomerInfo(undefined);
    fetchQuickBooksCustomerInformation();
  }, [accountId]);

  const fetchQuickBooksCustomerInformation = async () => {
    if (accountId) {
      setLoading(true);
      try {
        const { data: customerInfoResponse } = await getQuickBooksCustomerInfo(accountId);
        setCustomerInfo(customerInfoResponse);
      } catch (error) {
        setCustomerInfo(undefined);
        showToast.info(toastMessages.QB_CUSTOMER_NOT_FOUND);
      } finally {
        setLoading(false);
      }
    }
  };

  const getBillingAddress = () => {
    const hasBillingAddress: boolean =
      Boolean(customerInfo?.billAddressLine1) &&
      Boolean(customerInfo?.billAddressCity) &&
      Boolean(customerInfo?.billAddressCountry) &&
      Boolean(customerInfo?.billAddressPostalCode);

    if (hasBillingAddress) {
      return `${customerInfo?.billAddressLine1}, ${customerInfo?.billAddressCity}, ${customerInfo?.billAddressCountry} ${customerInfo?.billAddressPostalCode}`;
    }

    return '--';
  };

  return (
    <>
      <div className="qb-customer-info">
        <div className="header">
          <h2>{LABEL_CONSTANTS.QB_CUSTOMER}</h2>
          <button
            className="icon button transparent edit-button"
            data-testid="edit-button"
            disabled={loading}
            onClick={() => setIsOpenQbEditModal(true)}>
            <EditSVG />
          </button>
        </div>
        {loading ? (
          <Loader loaderSize={'small'} />
        ) : customerInfo ? (
          <>
            <div className="body">
              <LabeledValue label="Display Name" value={customerInfo?.displayName || '--'} />
              <LabeledValue label="Customer Reference Id" value={customerInfo?.customerRefId || '--'} />
              <LabeledValue label="Company Name" value={customerInfo?.companyName || '--'} />
              <LabeledValue label="Primary Email" value={customerInfo?.primaryEmail || '--'} />
              <LabeledValue label="Primary Phone" value={customerInfo?.primaryPhone || '--'} />
              <LabeledValue label="Billing Address" value={getBillingAddress()} />
            </div>
          </>
        ) : (
          <div className="no-association-message">
            {LABEL_CONSTANTS.QB_CUSTOMER_NOT_FOUND}
            <br />
          </div>
        )}
      </div>

      <QbEditModal
        isOpen={isOpenQbEditModal}
        setIsOpen={setIsOpenQbEditModal}
        accountId={accountId}
        customerInformation={customerInfo}
        onConfirm={fetchQuickBooksCustomerInformation}
      />
    </>
  );
};

export default QBCustomerInfo;
